import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageSquare, FiUser, FiTrash2, FiSearch, FiCalendar, FiStar, FiChevronRight, FiClock, FiBookOpen } = FiIcons;

const ReviewsManagement = () => {
  const { user } = useAuth();
  const { tutorReviews, studentReviews, deleteTutorReview, deleteStudentReview } = useData();
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions' | 'tutors'
  const [searchTerm, setSearchTerm] = useState('');

  const isAdmin = user?.role === 'admin';

  const handleDelete = async (id, type) => {
    if (!window.confirm("Permanently delete this entry?")) return;
    try {
      if (type === 'tutor') await deleteTutorReview(id);
      else await deleteStudentReview(id);
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  const filteredSessions = studentReviews.filter(r => 
    r.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    r.tutorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredTutors = tutorReviews.filter(r => 
    r.tutorName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    r.parentName?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const TabButton = ({ id, label, icon, count }) => (
    <button 
      onClick={() => setActiveTab(id)}
      className={`relative flex items-center gap-3 px-8 py-5 transition-all duration-300 ${
        activeTab === id ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'
      }`}
    >
      <SafeIcon icon={icon} className="text-lg" />
      <span className="font-black text-xs uppercase tracking-[0.2em]">{label}</span>
      {count > 0 && (
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
          activeTab === id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'
        }`}>
          {count}
        </span>
      )}
      {activeTab === id && (
        <motion.div 
          layoutId="activeTab" 
          className="absolute bottom-0 left-0 right-0 h-1 bg-indigo-600 rounded-full" 
        />
      )}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-10">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-6xl font-black text-gray-900 tracking-tighter">Feedback</h2>
          <p className="text-gray-400 font-bold uppercase tracking-[0.3em] text-xs mt-3 ml-1">
            Quality Assurance & Observations
          </p>
        </div>
        
        <div className="relative w-full md:w-96">
          <SafeIcon icon={FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search logs..." 
            className="w-full pl-14 pr-6 py-4 bg-white border-2 border-transparent focus:border-indigo-600 rounded-[1.5rem] shadow-sm outline-none font-bold text-gray-700 transition-all placeholder:text-gray-300"
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-[1.5rem] shadow-sm border border-gray-100 flex overflow-hidden overflow-x-auto no-scrollbar">
        <TabButton id="sessions" label="Session Reports" icon={FiClock} count={filteredSessions.length} />
        <TabButton id="tutors" label="Tutor Performance" icon={FiStar} count={filteredTutors.length} />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence mode="popLayout">
          {activeTab === 'sessions' ? (
            filteredSessions.map(report => (
              <motion.div 
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                key={report.id}
                className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-transparent hover:border-indigo-100 hover:shadow-2xl hover:shadow-indigo-100/20 transition-all group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                      <SafeIcon icon={FiCalendar} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                        {new Date(report.sessionDate || report.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </p>
                      <h4 className="font-black text-gray-900 uppercase tracking-tight">{report.subject}</h4>
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => handleDelete(report.id, 'student')} className="p-2 text-gray-200 hover:text-rose-500 transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  )}
                </div>

                <div className="bg-gray-50 rounded-[1.5rem] p-6 mb-6 relative">
                  <p className="text-gray-600 font-bold text-sm leading-relaxed italic">"{report.comment}"</p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-[10px] text-white font-black">
                      {report.tutorName?.charAt(0)}
                    </div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{report.tutorName}</span>
                  </div>
                  <div className="px-3 py-1 bg-white border border-gray-100 rounded-lg text-[9px] font-black text-indigo-600 uppercase tracking-widest">
                    For: {report.studentName}
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            filteredTutors.map(review => (
              <motion.div 
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                key={review.id}
                className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-transparent hover:border-emerald-100 hover:shadow-2xl hover:shadow-emerald-100/20 transition-all group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                      <SafeIcon icon={FiUser} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest leading-none mb-1">
                        Parent Satisfaction
                      </p>
                      <h4 className="font-black text-gray-900 uppercase tracking-tight">{review.tutorName}</h4>
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => handleDelete(review.id, 'tutor')} className="p-2 text-gray-200 hover:text-rose-500 transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  )}
                </div>

                <div className="bg-emerald-50/30 border border-emerald-50 rounded-[1.5rem] p-6 mb-6">
                  <p className="text-gray-700 font-bold text-sm leading-relaxed italic">"{review.comment}"</p>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">From: {review.parentName}</span>
                  <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>

        {((activeTab === 'sessions' ? filteredSessions : filteredTutors).length === 0) && (
          <div className="col-span-full py-32 text-center bg-white rounded-[3rem] border-4 border-dashed border-gray-50">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiMessageSquare} className="text-3xl text-gray-200" />
            </div>
            <p className="text-gray-400 font-black uppercase tracking-[0.2em]">Silence has been detected</p>
            <p className="text-xs text-gray-300 font-bold mt-2 uppercase">No records matching your search query</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsManagement;